import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { formatUnits, parseUnits } from 'viem';

// the store module only: the flow barrel carries the sign-out dialog.
import { selectBothFlowsUnlocked, useFlowStore } from '@/features/flow/store';
// deep import (not the wallet barrel): the header control that renders the
// Migrate button lives in the wallet feature.
import { useAuthedApi } from '@/features/wallet/hooks/useAuthedApi';
import { fetchMigrateQuote, USDC_DECIMALS } from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

const QUOTE_STALE_TIME_MS = 10_000;
const QUOTE_REFETCH_INTERVAL_MS = 20_000;

export interface MigrateQuoteState {
    /**
     * USD a migration started now would move (the 1.01 USDC HyperCore reserve
     * already left out); null while unknown: loading, failed, or not V1.
     */
    migratable: number | null;
    /** The same figure in USDC base units (6dp), exact. */
    migratableRaw: bigint | null;
    /** The account has a V1 custodial wallet at all. */
    hasWallet: boolean;
    /** USD held on HyperCore; above zero, the reserve stays behind there. */
    coreUsd: number | null;
    isLoading: boolean;
    /** Re-reads the quote; does nothing while the quote is not enabled. */
    refetch: () => void;
}

/** A plain decimal USD string as USDC base units, truncated to 6 decimals (never more). */
function baseUnitsOfUsd(usd: string): bigint {
    const [whole = '0', fraction = ''] = usd.split('.');
    return parseUnits(
        `${whole}.${fraction.slice(0, USDC_DECIMALS) || '0'}`,
        USDC_DECIMALS,
    );
}

/**
 * `GET /speed/migrate/quote`: what a V1 to V2 migration would move right now.
 * This, not `useSpeedBalance().numericWithdrawable`, is the figure migration
 * reads: the withdrawable one applies the withdrawal floor and the either/or
 * bucket rule, so it can read 0 while 0.99 USDC above the reserve is movable.
 *
 * Enabled only while V1 is the flow in use, both flows are unlocked and the
 * MoonHub authed client exists (in V2 `useAuthedApi` is null, so nothing V1
 * is asked). Interval refetches skip a hidden tab: React Query's focus
 * manager reads `document.visibilityState`.
 */
export function useMigrateQuote(): MigrateQuoteState {
    const isV1 = useFlowStore((state) => state.active === 'v1');
    const bothUnlocked = useFlowStore(selectBothFlowsUnlocked);
    const authedApi = useAuthedApi();
    const enabled = isV1 && bothUnlocked && authedApi !== null;

    const query = useQuery({
        queryKey: queryKeys.speed.migrateQuote(),
        queryFn: async ({ signal }) => fetchMigrateQuote(authedApi!, signal),
        enabled,
        staleTime: QUOTE_STALE_TIME_MS,
        refetchInterval: enabled ? QUOTE_REFETCH_INTERVAL_MS : false,
        refetchIntervalInBackground: false,
        retry: 1,
    });

    // stable for effect deps (see useSpeedBalance), and inert while disabled:
    // `refetch()` would run the query function even on a disabled query.
    const { refetch: runRefetch } = query;
    const refetch = useCallback(() => {
        if (enabled) void runRefetch();
    }, [enabled, runRefetch]);

    const derived = useMemo(() => {
        const data = enabled ? query.data : undefined;
        if (!data) {
            return {
                migratable: null,
                migratableRaw: null,
                hasWallet: false,
                coreUsd: null,
            };
        }
        const migratableRaw = data.hasWallet
            ? baseUnitsOfUsd(data.migratableUsd)
            : 0n;
        return {
            migratable: Number(formatUnits(migratableRaw, USDC_DECIMALS)),
            migratableRaw,
            hasWallet: data.hasWallet,
            coreUsd: Number(data.coreUsd),
        };
    }, [enabled, query.data]);

    return {
        ...derived,
        isLoading: enabled && query.isLoading,
        refetch,
    };
}
